import {
  ComplianceDocument,
  FolderStructureResponse,
  MarketingAssetFileDetailSchema,
} from '@/lib/types';
import FileListTable from './FileListTable';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState, ReactNode } from 'react';
import Button from '../Button/Button';
import { FormattedMessage } from 'react-intl';
import DownloadIcon from '@/assets/download.svg?react';
import TrashIcon from '@/assets/trash.svg?react';
import ModeSwitch from './ModeSwitch';
import FileGrid from './FileGrid';
import FilterIcon from '@/assets/filter.svg?react';
import Popper from '@/components/Popper/Popper';
import FileExplorerComplianceFilters from './FileExplorerComplianceFilters';
import FileExplorerMarketingFilters from './FileExplorerMarketingFilters';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { flattenDeep } from 'lodash';
import { ComplianceFileTypeConfig } from '@/constants/FileTypesCompliance';

const FileExplorer = ({
  loading,
  files,
  filesFlatList,
  selected,
  onSelect,
  onDownloadClick,
  onRemoveClick,
  showDownload = true,
  selectedCount,
  showSelectedLabel = true,
  showRemove = false,
  type = 'compliance',
  showFilters,
  filters,
  onFiltersChange,
  fileTypeComplianceConfig,
  showHeader = true,
  path,
  onPathChange,
  onFileDelete,
  hiddenRoot,
  downloadLoading,
  removeLoading,
  loadingNextPage,
  actions,
  hasActiveSearchOrFilter,
  currentView,
  onViewChange,
  exactMatchQuery,
}: {
  folderStructure?: FolderStructureResponse;
  loading?: boolean;
  files?: ComplianceDocument[] | MarketingAssetFileDetailSchema[];
  filesFlatList?:
    | ComplianceDocument[]
    | MarketingAssetFileDetailSchema[];
  selected?: number[];
  onSelect?: (selected: number[]) => void;
  onDownloadClick?: () => void;
  onRemoveClick?: () => void;
  showDownload?: boolean;
  // Overrides the count shown in the label and used to enable the action
  // buttons - lets a host scope them to the rows on screen when `selected`
  // is a wider, app-level selection.
  selectedCount?: number;
  showSelectedLabel?: boolean;
  showRemove?: boolean;
  type?: 'compliance' | 'marketing' | 'game';
  showFilters?: boolean;
  filters?: Partial<FilterSchema>;
  onFiltersChange?: (filters: Partial<FilterSchema>) => void;
  fileTypeComplianceConfig?: ComplianceFileTypeConfig;
  showHeader?: boolean;
  path: string[];
  onPathChange: (path: string[]) => void;
  onFileDelete?: () => void;
  hiddenRoot?: string;
  downloadLoading?: boolean;
  removeLoading?: boolean;
  loadingNextPage?: boolean;
  actions?: ReactNode;
  hasActiveSearchOrFilter?: boolean;
  onAllFilesClick?: () => void;
  currentView?: 'list' | 'grid';
  onViewChange?: (view: 'list' | 'grid') => void;
  exactMatchQuery?: string;
}) => {
  const effectiveSelectedCount =
    selectedCount ?? selected?.length ?? 0;
  const filterRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<'list' | 'grid'>(
    currentView ?? 'grid',
  );

  useEffect(() => {
    if (currentView) {
      setMode(currentView);
    }
  }, [currentView]);

  const handleModeChange = (newMode: 'list' | 'grid') => {
    if (!currentView) {
      setMode(newMode);
    }
    onViewChange?.(newMode);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showNotification =
    flattenDeep<
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(Object.values(filters || {})).length > 0;

  useEffect(() => {
    if (path.length === 0 && hiddenRoot) {
      onPathChange([hiddenRoot]);
    }
  }, [path, files, hiddenRoot]);

  return (
    <Root aria-label="File explorer">
      {showHeader && (
        <Header>
          <Actions>
            {showSelectedLabel && effectiveSelectedCount ? (
              <SelectedLabel>
                <FormattedMessage
                  id="itemsSelected"
                  defaultMessage="{count} items selected"
                  values={{ count: <b>{effectiveSelectedCount}</b> }}
                />
              </SelectedLabel>
            ) : null}
            {actions ? (
              <ActionButtons>{actions}</ActionButtons>
            ) : null}
            {showDownload ? (
              <Button
                variant="contained"
                onClick={onDownloadClick}
                disabled={!effectiveSelectedCount}
                aria-label="Download selected"
                loading={downloadLoading}
              >
                <DownloadIcon />
                <FormattedMessage
                  id="downloadWithCount"
                  defaultMessage="Download{suffix}"
                  values={{
                    suffix: effectiveSelectedCount
                      ? ` (${effectiveSelectedCount})`
                      : '',
                  }}
                />
              </Button>
            ) : null}
            {showRemove ? (
              <Button
                variant="outlined"
                onClick={onRemoveClick}
                disabled={!effectiveSelectedCount}
                aria-label="Remove selected"
                loading={removeLoading}
              >
                <TrashIcon />
                <FormattedMessage
                  id="remove"
                  defaultMessage="Remove"
                />
              </Button>
            ) : null}
            {showFilters ? (
              <>
                <Button
                  variant="outlined"
                  icon
                  ref={filterRef}
                  onClick={() => setFiltersOpen((v) => !v)}
                  aria-label="Filter Button"
                >
                  <FilterIcon />
                  {showNotification && <FilterNotification />}
                </Button>
                <Popper
                  aria-label="Filters Popper"
                  open={filtersOpen}
                  anchorEl={filterRef.current}
                  onClose={() => setFiltersOpen(false)}
                  offsetY={10}
                >
                  {type === 'compliance' &&
                  filters &&
                  onFiltersChange ? (
                    <FileExplorerComplianceFilters
                      filters={filters}
                      onFiltersChange={onFiltersChange}
                      fileTypeConfig={fileTypeComplianceConfig}
                    />
                  ) : null}
                  {type === 'marketing' &&
                  filters &&
                  onFiltersChange ? (
                    <FileExplorerMarketingFilters
                      filters={filters}
                      onFiltersChange={onFiltersChange}
                    />
                  ) : null}
                </Popper>
              </>
            ) : null}
            {onViewChange ? (
              <ModeSwitch mode={mode} onChange={handleModeChange} />
            ) : null}
          </Actions>
        </Header>
      )}
      {mode === 'list' ? (
        <FileListTable
          files={[...(filesFlatList ?? files ?? [])]}
          loading={loading}
          loadingNextPage={loadingNextPage}
          type={type === 'compliance' ? 'compliance' : 'marketing'}
          selected={selected}
          onSelect={onSelect}
          onFileDelete={onFileDelete}
          downloadLoading={downloadLoading}
          hasActiveSearchOrFilter={hasActiveSearchOrFilter}
          exactMatchQuery={exactMatchQuery}
        />
      ) : (
        <FileGrid
          files={[...(filesFlatList ?? files ?? [])]}
          selected={selected}
          onSelect={onSelect}
          type={type}
          loading={loading}
          exactMatchQuery={exactMatchQuery}
        />
      )}
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginLeft: 'auto',
  alignItems: 'center',
}));

const ActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const SelectedLabel = styled('div')(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  color: theme.palette.grey500,

  b: {
    fontWeight: 500,
  },
}));

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

// const LoaderContainer = styled('div')(() => ({
//   width: '100%',
//   padding: '60px',
//   display: 'flex',
//   justifyContent: 'center',
// }));

export default FileExplorer;
